import { Component, OnInit, Output, EventEmitter, Input, OnChanges, SimpleChanges, OnDestroy } from '@angular/core';
import { PriceRange, PriceBounds, clampPriceRange, clampValue } from 'src/app/entities/price-range';
import { BrandFilter } from 'src/app/entities/brand';

/**
 * Filter output for price range changes.
 * Includes both the applied range and whether a filter is active.
 */
export interface PriceFilterOutput {
  from: number;
  to: number;
  isActive: boolean;
}

@Component({
  selector: 'app-filters',
  templateUrl: './filters.component.html',
  styleUrls: ['./filters.component.scss']
})
export class FiltersComponent implements OnInit, OnChanges, OnDestroy {

  // Input: Price bounds from parent (catalog and brand-specific)
  @Input() priceBounds: PriceBounds | null = null;
  @Input() priceRange: PriceRange = { min: null, max: null }; // Kept for backward compatibility
  @Input() brands: BrandFilter[] = [];
  @Input() selectedBrandIds: number[] = [];
  @Input() isPriceFilterActiveFromParent: boolean = false;
  
  // Outputs
  @Output() applyPrice = new EventEmitter<{from: number, to: number}>();
  @Output() applyDiscount = new EventEmitter<any>();
  @Output() applyStock = new EventEmitter<any>();
  @Output() applyBrand = new EventEmitter<number[]>();

  filterStock = true;
  
  // Brand filter local state
  isBrandFilterActive: boolean = false;
  brandSearchText: string = '';
  showAllBrands: boolean = false;
  visibleBrandsLimit: number = 8;
  
  // ===== Price Filter State =====
  // Catalog bounds (from all products)
  catalogMinPrice: number = 0;
  catalogMaxPrice: number = 0;
  
  // Brand-specific bounds (from selected brands, or catalog if none)
  brandMinPrice: number = 0;
  brandMaxPrice: number = 0;
  
  // UI state
  minPriceInput: number | null = null;  // Raw user input for min (null = not set by user)
  maxPriceInput: number | null = null;  // Raw user input for max (null = not set by user)
  priceRangeApplied: [number, number] = [0, 0]; // Normalized, applied to query
  
  // Slider state (driven by brand bounds)
  sliderMin: number = 0;
  sliderMax: number = 0;
  minPrice: number = 0;  // Current slider min position (for display)
  maxPrice: number = 0;  // Current slider max position (for display)
  
  // Internal state tracking
  private debounceTimer: any;
  private lastAppliedMin: number = 0;
  private lastAppliedMax: number = 0;
  hasEverAppliedFilter: boolean = false;
  isPriceFilterActive: boolean = false;
  private hasInitialized: boolean = false;

  constructor() { }

  ngOnInit() {
  }
  
  ngOnDestroy() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    // Check if parent has reset the price filter
    if (changes.isPriceFilterActiveFromParent && 
        this.isPriceFilterActiveFromParent === false && 
        this.isPriceFilterActive === true) {
      // Parent has cleared filters - reset our local state WITHOUT emitting
      this.isPriceFilterActive = false;
      this.hasEverAppliedFilter = false;
      this.minPriceInput = null;
      this.maxPriceInput = null;
      // Slider will be reset when new bounds arrive
    }
    
    // Handle new priceBounds input (preferred)
    if (changes.priceBounds && this.priceBounds) {
      this.handlePriceBoundsChange();
    }
    // Fallback to legacy priceRange input
    else if (changes.priceRange && this.priceRange && !this.priceBounds) {
      this.initializePriceRange();
    }
    
    // Track brand filter state from parent and update bounds accordingly
    if (changes.selectedBrandIds) {
      const prevBrandIds = changes.selectedBrandIds.previousValue || [];
      const currBrandIds = changes.selectedBrandIds.currentValue || [];
      
      this.isBrandFilterActive = currBrandIds.length > 0;
      
      // If brand selection changed, we need to update price bounds
      // The parent will provide new priceBounds, but we track state here
      if (JSON.stringify(prevBrandIds) !== JSON.stringify(currBrandIds) && this.hasInitialized) {
        // Price range will be clamped when new priceBounds arrives
      }
    }
  }

  /**
   * Handle price bounds change from parent.
   * NOTE: This method only updates local state, it does NOT emit events.
   * The parent already called loadSearch() - we don't want to trigger another one.
   */
  handlePriceBoundsChange() {
    const bounds = this.priceBounds!;
    
    // Update catalog bounds
    this.catalogMinPrice = bounds.catalogMinPrice;
    this.catalogMaxPrice = bounds.catalogMaxPrice;
    
    // Update brand-specific bounds
    this.brandMinPrice = bounds.brandMinPrice;
    this.brandMaxPrice = bounds.brandMaxPrice;
    
    // Slider limits are driven by brand bounds
    this.sliderMin = this.brandMinPrice;
    this.sliderMax = this.brandMaxPrice;
    
    if (!this.hasInitialized) {
      // First initialization: NO price filter active by default
      // Slider positions show full range
      this.minPrice = this.brandMinPrice;
      this.maxPrice = this.brandMaxPrice;
      // User inputs remain null (will show placeholders from API)
      this.minPriceInput = null;
      this.maxPriceInput = null;
      this.isPriceFilterActive = false;
      this.hasInitialized = true;
    } else if (this.isPriceFilterActive) {
      // Price filter was active: clamp current price range to new bounds
      const [newMin, newMax] = clampPriceRange(
        this.priceRangeApplied[0],
        this.priceRangeApplied[1],
        this.brandMinPrice,
        this.brandMaxPrice
      );
      
      this.priceRangeApplied = [newMin, newMax];
      this.minPrice = newMin;
      this.maxPrice = newMax;
      this.minPriceInput = newMin;
      this.maxPriceInput = newMax;
      this.lastAppliedMin = newMin;
      this.lastAppliedMax = newMax;
      
      // Keep filter active - user explicitly applied it
      // They need to click "Clear" to remove it, even if bounds changed
      // The filter values are still being applied to the query
    } else {
      // Price filter was not active - just update slider to show full range
      this.minPrice = this.brandMinPrice;
      this.maxPrice = this.brandMaxPrice;
    }
  }

  /**
   * Legacy initialization from priceRange input.
   * Used when priceBounds is not provided.
   */
  initializePriceRange() {
    // Set the slider bounds from API data
    this.sliderMin = this.priceRange.min ?? 0;
    this.sliderMax = this.priceRange.max ?? 1000000;
    
    // Also set as catalog and brand bounds (legacy behavior)
    this.catalogMinPrice = this.sliderMin;
    this.catalogMaxPrice = this.sliderMax;
    this.brandMinPrice = this.sliderMin;
    this.brandMaxPrice = this.sliderMax;
    
    // Slider shows full range
    this.minPrice = this.sliderMin;
    this.maxPrice = this.sliderMax;
    
    if (!this.hasInitialized) {
      // NO price filter active by default - inputs remain null (show placeholders)
      this.minPriceInput = null;
      this.maxPriceInput = null;
      this.isPriceFilterActive = false;
      this.hasInitialized = true;
    }
  }

  onChangeFilterStock() {
    this.applyStock.emit(this.filterStock);
  }

  // ===== Price Input Handlers =====
  
  onMinPriceChange() {
    // Ensure min doesn't exceed max
    if (this.minPriceInput > this.maxPriceInput) {
      this.minPriceInput = this.maxPriceInput;
    }
    // Clamp within brand bounds
    this.minPriceInput = clampValue(this.minPriceInput, this.brandMinPrice, this.brandMaxPrice);
    this.minPrice = this.minPriceInput;
    this.applyPriceFilter();
  }

  onMaxPriceChange() {
    // Ensure max doesn't go below min
    if (this.maxPriceInput < this.minPriceInput) {
      this.maxPriceInput = this.minPriceInput;
    }
    // Clamp within brand bounds
    this.maxPriceInput = clampValue(this.maxPriceInput, this.brandMinPrice, this.brandMaxPrice);
    this.maxPrice = this.maxPriceInput;
    this.applyPriceFilter();
  }

  /**
   * Handle min price input blur 
   * Validates and clamps input, handles empty values.
   */
  onMinPriceInputBlur(event: any) {
    const inputValue = event.target.value.trim();
    
    // If empty, keep as null (no filter) - use placeholder
    if (inputValue === '') {
      this.minPriceInput = null;
      this.minPrice = this.brandMinPrice; // Slider shows full range
      return;
    }
    
    const value = this.parseNumberInput(inputValue);
    
    // Clamp to brand bounds 
    this.minPriceInput = clampValue(value, this.brandMinPrice, this.brandMaxPrice);

    // If max is set, ensure min <= max, swap if needed
    if (this.maxPriceInput !== null && this.minPriceInput > this.maxPriceInput) {
      const temp = this.minPriceInput;
      this.minPriceInput = this.maxPriceInput;
      this.maxPriceInput = temp;
    }
    
    this.minPrice = this.minPriceInput;
  }

  /**
   * Handle max price input blur 
   * Validates and clamps input, handles empty values.
   */
  onMaxPriceInputBlur(event: any) {
    const inputValue = event.target.value.trim();
    
    // If empty, keep as null (no filter) - use placeholder
    if (inputValue === '') {
      this.maxPriceInput = null;
      this.maxPrice = this.brandMaxPrice; // Slider shows full range
      return;
    }
    
    const value = this.parseNumberInput(inputValue);
    
    // Clamp to brand bounds 
    this.maxPriceInput = clampValue(value, this.brandMinPrice, this.brandMaxPrice);
    
    // If min is set, ensure min <= max, swap if needed 
    if (this.minPriceInput !== null && this.maxPriceInput < this.minPriceInput) {
      const temp = this.minPriceInput;
      this.minPriceInput = this.maxPriceInput;
      this.maxPriceInput = temp;
    }
    
    this.maxPrice = this.maxPriceInput;
  }

  parseNumberInput(value: string): number {
    // Remove all non-numeric characters except decimal point
    const cleanValue = value.replace(/[^0-9]/g, '');
    return cleanValue ? parseInt(cleanValue, 10) : 0;
  }

  // ===== Slider Handlers =====

  /**
   * Handle min slider input
   * Thumbs cannot cross; snap if user drags past other thumb.
   */
  onMinSliderInput(event: Event) {
    const input = event.target as HTMLInputElement;
    const value = parseInt(input.value, 10);
    
    // Prevent min from exceeding max (thumbs cannot cross)
    if (value > this.maxPrice) {
      this.minPrice = this.maxPrice;
      input.value = String(this.maxPrice);
    } else {
      this.minPrice = value;
    }
    this.minPriceInput = this.minPrice;
  }

  /**
   * Handle max slider input 
   * Thumbs cannot cross; snap if user drags past other thumb.
   */
  onMaxSliderInput(event: Event) {
    const input = event.target as HTMLInputElement;
    const value = parseInt(input.value, 10);
    
    // Prevent max from going below min (thumbs cannot cross)
    if (value < this.minPrice) {
      this.maxPrice = this.minPrice;
      input.value = String(this.minPrice);
    } else {
      this.maxPrice = value;
    }
    this.maxPriceInput = this.maxPrice;
  }

  onMinSliderChange(value: number) {
    this.minPrice = Math.min(value, this.maxPrice);
    this.minPriceInput = this.minPrice;
  }

  onMaxSliderChange(value: number) {
    this.maxPrice = Math.max(value, this.minPrice);
    this.maxPriceInput = this.maxPrice;
  }

  onSliderInput() {
    this.minPriceInput = this.minPrice;
    this.maxPriceInput = this.maxPrice;
  }

  onSliderChange() {
    this.onSliderInput();
  }

  // ===== Range Track Calculations =====
  
  getRangeLeft(): string {
    const range = this.sliderMax - this.sliderMin;
    if (range <= 0) return '0%';
    const left = ((this.minPrice - this.sliderMin) / range) * 100;
    return `${left}%`;
  }

  getRangeWidth(): string {
    const range = this.sliderMax - this.sliderMin;
    if (range <= 0) return '100%';
    const width = ((this.maxPrice - this.minPrice) / range) * 100;
    return `${width}%`;
  }

  /**
   * Apply price filter immediately
   * Called when user clicks the apply button.
   */
  applyPriceFilterNow() {
    // Clear any pending debounce
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
    
    // Determine effective min/max based on user input
    // If user only set one value, use brand bound for the other
    const effectiveMin = this.minPriceInput !== null ? this.minPriceInput : this.brandMinPrice;
    const effectiveMax = this.maxPriceInput !== null ? this.maxPriceInput : this.brandMaxPrice;
    
    // Update slider positions
    this.minPrice = effectiveMin;
    this.maxPrice = effectiveMax;
    
    // Update inputs to show the effective values
    this.minPriceInput = effectiveMin;
    this.maxPriceInput = effectiveMax;
    
    // Update last applied values
    this.lastAppliedMin = effectiveMin;
    this.lastAppliedMax = effectiveMax;
    
    // Update applied range
    this.priceRangeApplied = [effectiveMin, effectiveMax];
    
    // Check if we're actually applying a filter (different from brand bounds)
    const isFilterApplied = effectiveMin !== this.brandMinPrice || effectiveMax !== this.brandMaxPrice;
    
    if (isFilterApplied) {
      this.hasEverAppliedFilter = true;
      this.isPriceFilterActive = true;
    } else {
      this.isPriceFilterActive = false;
    }
    
    this.applyPriceFilter();
  }

  hasChanges(): boolean {
    // If filter is not active, check if user has set any input values
    if (!this.isPriceFilterActive) {
      return this.minPriceInput !== null || this.maxPriceInput !== null;
    }
    // If filter is active, check if slider position differs from applied range
    return this.minPrice !== this.lastAppliedMin || this.maxPrice !== this.lastAppliedMax;
  }

  getSliderStep(): number {
    // Calculate smart step based on range
    const range = this.sliderMax - this.sliderMin;
    if (range > 10000000) return 100000; // 100k step for very large ranges
    if (range > 1000000) return 10000;   // 10k step for large ranges
    if (range > 100000) return 5000;     // 5k step for medium ranges
    return 1000;                          // 1k step for smaller ranges
  }

  hasActiveFilter(): boolean {
    return this.isPriceFilterActive;
  }

  /**
   * Emit price filter to parent.
   */
  applyPriceFilter() {
    // Only send price filter if it's different from the brand bounds (full range)
    if (this.minPrice === this.brandMinPrice && this.maxPrice === this.brandMaxPrice) {
      // No filter - remove it completely
      this.applyPrice.emit({ from: 0, to: 0 });
    } else {
      // Apply the filter
      this.applyPrice.emit({ from: this.minPrice, to: this.maxPrice });
    }
  }

  /**
   * Reset price filter.
   * Reset to brand bounds, keep brand selections intact.
   */
  resetPriceFilter() {
    // Clear any pending debounce timer first
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
    
    // Reset flags FIRST
    this.hasEverAppliedFilter = false;
    this.isPriceFilterActive = false;
    
    // Reset slider to full brand bounds
    this.minPrice = this.brandMinPrice;
    this.maxPrice = this.brandMaxPrice;
    
    // Clear user inputs (show placeholders again)
    this.minPriceInput = null;
    this.maxPriceInput = null;
    
    // Reset tracking
    this.lastAppliedMin = this.brandMinPrice;
    this.lastAppliedMax = this.brandMaxPrice;
    this.priceRangeApplied = [this.brandMinPrice, this.brandMaxPrice];
    
    // Slider bounds stay at brand bounds (they're already correct)
    this.sliderMin = this.brandMinPrice;
    this.sliderMax = this.brandMaxPrice;
    
    // Emit to remove filter - parent will call removeWhere
    this.applyPrice.emit({ from: 0, to: 0 });
  }

  // ===== Brand Filter Methods =====
  
  /**
   * Handle brand checkbox click.
   * User can select multiple brands.
   */
  onBrandClick(brandId: number) {
    let newSelection: number[];
    
    if (this.isBrandSelected(brandId)) {
      // Remove brand from selection
      newSelection = this.selectedBrandIds.filter(id => id !== brandId);
    } else {
      // Add brand to selection
      newSelection = [...this.selectedBrandIds, brandId];
    }
    
    this.isBrandFilterActive = newSelection.length > 0;
    this.applyBrand.emit(newSelection);
  }

  isBrandSelected(brandId: number): boolean {
    return this.selectedBrandIds && this.selectedBrandIds.includes(brandId);
  }

  /**
   * Clear brand filter.
   * Interpret as "all brands", not "no products".
   */
  clearBrandFilter() {
    this.isBrandFilterActive = false;
    this.brandSearchText = '';
    this.showAllBrands = false;
    this.applyBrand.emit([]);
  }

  // ===== Brand Search & Display Methods =====

  /**
   * Filter brands by search text
   */
  getFilteredBrands(): BrandFilter[] {
    if (!this.brandSearchText) {
      return this.brands;
    }
    const searchLower = this.brandSearchText.toLowerCase().trim();
    return this.brands.filter(brand => 
      brand.name.toLowerCase().includes(searchLower)
    );
  }

  /**
   * Get selected brands (for showing at top)
   */
  getSelectedBrands(): BrandFilter[] {
    return this.brands.filter(brand => this.isBrandSelected(brand.id));
  }

  /**
   * Get unselected brands
   */
  getUnselectedBrands(): BrandFilter[] {
    return this.brands.filter(brand => !this.isBrandSelected(brand.id));
  }

  /**
   * Get visible brands based on search, selection, and show all state
   */
  getVisibleBrands(): BrandFilter[] {
    // If searching, show all filtered results (excluding already shown selected)
    if (this.brandSearchText) {
      return this.getFilteredBrands();
    }

    // Not searching: show unselected brands (selected are shown separately at top)
    const unselected = this.getUnselectedBrands();
    
    // If showing all, return all unselected
    if (this.showAllBrands) {
      return unselected;
    }

    // Otherwise limit to visibleBrandsLimit
    return unselected.slice(0, this.visibleBrandsLimit);
  }

  /**
   * Get total count of unselected brands
   */
  getTotalUnselectedCount(): number {
    return this.getUnselectedBrands().length;
  }

  /**
   * Handle brand search input change
   */
  onBrandSearchChange() {
    // Reset show all when searching
    this.showAllBrands = false;
  }

  /**
   * Clear brand search
   */
  clearBrandSearch() {
    this.brandSearchText = '';
    this.showAllBrands = false;
  }

  /**
   * Toggle show all brands
   */
  toggleShowAllBrands() {
    this.showAllBrands = !this.showAllBrands;
  }
}
